/**
 * What: The pure step that moves browser-tracked StudyRuns on after each activity poll.
 * Transport / data-flow: ShopifyStudyActivityContext calls it with the stored state and the latest poll snapshot.
 */
import { classifyStudyRunStatus } from './components/study-run-status'
import type { ShopifyStudyActivitySnapshot } from './hooks/useShopifyStudyActivityQuery'

export type NotificationState = {
  pendingRunIds: string[]
  unreadRunIds: string[]
  seenRunIds: string[]
  studyIdByRunId: Record<string, string>
}

export function uniqueIds(ids: unknown) {
  return Array.isArray(ids)
    ? [...new Set(ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0))]
    : []
}

/** Ready runs become unread, settled runs stop polling, and runs the server says are gone are forgotten entirely. */
export function advanceNotificationState(current: NotificationState, snapshot: ShopifyStudyActivitySnapshot) {
  const readyRunIds = current.pendingRunIds.filter((runId) => {
    // A previous successful response must not hide a new status failure.
    if (snapshot.errorsByRunId[runId]) return false
    const report = snapshot.reportsByRunId[runId]
    return report?.selectedRunId === runId && report.runs.some((run) => run.id === runId && run.reportReady)
  })
  const terminalRunIds = current.pendingRunIds.filter((runId) => {
    if (snapshot.errorsByRunId[runId]) return false
    const run = snapshot.reportsByRunId[runId]?.runs.find((candidate) => candidate.id === runId)
    if (!run) return false
    const status = classifyStudyRunStatus(run.status, run.latestStepKey, run.reportReady)
    return status === 'needs-attention' || status === 'stopped' || status === 'superseded'
  })
  // The server's 404 means the run or its test is gone (for example deleted): stop tracking it entirely.
  const goneRunIds = current.pendingRunIds.filter((runId) => snapshot.goneRunIds.includes(runId))
  const next: NotificationState = {
    ...current,
    pendingRunIds: current.pendingRunIds.filter(
      (runId) => !readyRunIds.includes(runId) && !terminalRunIds.includes(runId) && !goneRunIds.includes(runId),
    ),
    studyIdByRunId: Object.fromEntries(
      Object.entries(current.studyIdByRunId).filter(([runId]) => !goneRunIds.includes(runId)),
    ),
    unreadRunIds: uniqueIds([...current.unreadRunIds.filter((runId) => !goneRunIds.includes(runId)), ...readyRunIds]),
    seenRunIds: uniqueIds([...current.seenRunIds, ...readyRunIds]),
  }
  return { next, newlyReadyRunIds: readyRunIds.filter((runId) => !current.seenRunIds.includes(runId)) }
}
